
import React from 'react';
import { Gift, Clock, Tag } from 'lucide-react';
import TranslatedText from '@/TranslatedText';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';

// Example seasonal promotions data
const promotions = [
  {
    id: 1,
    title: 'Back to School Sale',
    description: 'Get ready for the new school year with great deals on all school supplies and electronics.',
    validUntil: new Date('2025-09-15'),
    imageSrc: '/placeholder.svg',
    url: '/search?promotion=back-to-school',
    discount: '30% Off',
    badge: 'Limited Time'
  },
  {
    id: 2,
    title: 'Local Craft Festival',
    description: 'Celebrate Rwandan craftsmanship with special prices on locally made products.',
    validUntil: new Date('2025-10-20'),
    imageSrc: '/placeholder.svg',
    url: '/search?promotion=local-craft-fest',
    discount: 'Up to 25% Off',
    badge: 'Made in Rwanda'
  }
];

const SpecialPromotions: React.FC = () => {
  return (
    <div className="py-12 border-t border-gray-200">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-ahavex-blue flex items-center">
          <Gift className="mr-2 text-purple-600" size={28} />
          <TranslatedText text="Special Promotions" />
        </h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {promotions.map((promo) => (
          <div 
            key={promo.id} 
            className="relative overflow-hidden rounded-xl shadow-md bg-gradient-to-r from-purple-50 to-blue-50"
          >
            <div className="md:flex h-full">
              <div className="md:w-2/5 h-48 md:h-full relative">
                <img 
                  src={promo.imageSrc} 
                  alt={promo.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-red-600 text-white px-3 py-1 text-sm">
                    <TranslatedText text={promo.discount} />
                  </Badge>
                </div>
                {promo.badge && (
                  <div className="absolute top-4 right-4">
                    <Badge variant="outline" className="bg-white border-ahavex-blue text-ahavex-blue px-3 py-1 text-sm">
                      <TranslatedText text={promo.badge} />
                    </Badge>
                  </div>
                )}
              </div>
              
              <div className="p-6 md:w-3/5 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-bold text-ahavex-blue mb-3">
                    <TranslatedText text={promo.title} />
                  </h3>
                  <p className="text-gray-600 mb-4">
                    <TranslatedText text={promo.description} />
                  </p>
                  <div className="flex items-center text-gray-500 text-sm mb-4">
                    <Clock size={14} className="mr-1" />
                    <span>
                      <TranslatedText text="Ends on:" /> {promo.validUntil.toLocaleDateString()}
                    </span>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <Button asChild className="bg-ahavex-blue hover:bg-ahavex-darkblue flex-1">
                    <Link to={promo.url}>
                      <TranslatedText text="Shop Now" />
                    </Link>
                  </Button>
                  <Button variant="outline" className="border-ahavex-blue text-ahavex-blue">
                    <TranslatedText text="Learn More" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-12 text-center">
        <div className="inline-block bg-amber-50 p-6 rounded-lg border border-amber-200">
          <div className="flex items-center justify-center mb-4 text-amber-600">
            <Tag size={24} className="mr-2" />
            <h3 className="text-xl font-bold">
              <TranslatedText text="Looking for more ways to save?" />
            </h3>
          </div>
          <p className="text-gray-700 mb-4 max-w-xl">
            <TranslatedText 
              text="Subscribe to our newsletter to get notified about upcoming promotions and exclusive deals before anyone else."
            />
          </p>
          <Button className="bg-amber-600 hover:bg-amber-700">
            <TranslatedText text="Subscribe to Deals" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SpecialPromotions;
